import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileDown, Phone, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { asArray, errMsg, http } from "@/lib/api";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "");
const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

const exportCsv = (rows) => {
  const head = ["Date", "Phone", "Name", "City", "WhatsApp", "Brand", "Price list", "Format"];
  const lines = rows.map((r) => [when(r.created_at), r.phone, r.name, r.city, r.whatsapp ? "Yes" : "", r.brand_name, r.doc_title, r.format].map(csvCell).join(","));
  const blob = new Blob([[head.join(","), ...lines].join("\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `pricehub-leads-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

const LeadRow = ({ r, onDelete }) => (
  <li className="flex items-start gap-3 py-3" data-testid={`admin-lead-${r.id}`}>
    <div className="min-w-0 flex-1">
      <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-ph-ink">
        <a href={`tel:${r.phone}`} className="underline" data-testid={`admin-lead-phone-${r.id}`}>{r.phone}</a>
        {r.whatsapp && <a href={`https://wa.me/${r.phone.replace(/^\+/, "")}`} target="_blank" rel="noreferrer" className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700 dark:text-emerald-300">WhatsApp</a>}
        {r.name && <span className="font-normal text-ph-ink/85">{r.name}</span>}
        {r.city && <span className="font-normal text-ph-muted">· {r.city}</span>}
      </p>
      <p className="text-xs text-ph-muted"><Link to={`/brands/${r.brand_slug}`} className="underline">{r.brand_name} — {r.doc_title}</Link> · {r.format?.toUpperCase()} · {when(r.created_at)}</p>
    </div>
    <button type="button" onClick={() => onDelete(r)} aria-label="Delete lead" data-testid={`admin-lead-delete-${r.id}`} className="shrink-0 rounded-full p-2 text-ph-muted hover:bg-ph-raised hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
  </li>
);

export function LeadsManager() {
  const [leads, setLeads] = useState(null);
  const load = useCallback(() => {
    http.get("/admin/leads").then(({ data }) => setLeads(asArray(data))).catch((e) => { setLeads([]); toast.error(errMsg(e)); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (r) => {
    if (!window.confirm(`Delete the enquiry from ${r.phone}?`)) return;
    try { await http.delete(`/admin/leads/${r.id}`); toast.success("Lead deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <section className="rounded-3xl border border-ph-line bg-ph-surface p-6" data-testid="admin-leads">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 font-display text-lg font-bold text-ph-ink"><Phone className="h-4 w-4 text-ph-accent" /> Price enquiries <span className="font-mono-ph text-sm text-ph-muted" data-testid="admin-leads-count">{leads?.length ?? ""}</span></p>
          <p className="text-sm text-ph-muted">Numbers left by visitors when downloading a price list. Each one is also emailed to you.</p>
        </div>
        <button type="button" disabled={!leads?.length} onClick={() => exportCsv(leads)} data-testid="admin-leads-export" className="inline-flex items-center gap-1.5 rounded-full border border-ph-line px-4 py-2 text-sm font-semibold text-ph-ink hover:bg-ph-raised disabled:opacity-50"><FileDown className="h-4 w-4" /> Export CSV</button>
      </div>
      {leads === null ? <div className="ph2-shimmer mt-4 h-24 rounded-2xl" /> : leads.length === 0 ? (
        <p className="mt-4 rounded-2xl border border-dashed border-ph-line py-10 text-center text-sm text-ph-muted" data-testid="admin-leads-empty">No enquiries yet.</p>
      ) : (
        <ul className="mt-4 divide-y divide-ph-line">{leads.map((r) => <LeadRow key={r.id} r={r} onDelete={remove} />)}</ul>
      )}
    </section>
  );
}
